import { stringifyUrl } from "query-string";
import React, { useCallback } from "react";
import { Link } from "react-router-dom";
import DisplayLabels from "../../../components/labels/DisplayLabels";
import StatusDot from "../../../components/StatusDot";
import { useHotspots } from "../../../context/hotspots";
import { useLocations } from "../../../context/locations";
import { avatarsApi } from "../../../utils/constants";
import { formatDateToLocale } from "../../../utils/dates";
import { formatHotspotName } from "../../../utils/hotspotUtils";
import { truncateText } from "../../../utils/text";
import { Install } from "../../../utils/types";
import MemberCell from "../../Workspace/common/MemberCell";

interface Props {
  installs: Install[];
}

const LinkedInstallsTable: React.FC<Props> = ({ installs }) => {
  const { locations } = useLocations();
  const { hotspotsMap } = useHotspots();

  const renderInstallTableRow = useCallback(
    ({
      uuid,
      hotspot_uuid,
      location_uuid,
      created_at,
      created_by,
      installed_at,
      uninstalled_at
    }: Install) => {
      const location = locations[location_uuid];
      const hotspot = hotspotsMap[hotspot_uuid];

      if (!location || !hotspot) return null;

      return (
        <tr key={uuid}>
          <td className="px-6 py-4 whitespace-nowrap">
            <Link
              className="relative flex items-center space-x-3 group text-sm font-medium text-gray-900 dark:text-white"
              to={`/installs/${uuid}/info`}
            >
              <div className="flex-shrink-0">
                <span className="inline-block relative">
                  <img
                    className="h-10 w-10 rounded-full"
                    src={stringifyUrl({
                      url: `${avatarsApi}/bottts/${hotspot_uuid}.svg`,
                      query: { w: 40, h: 40 }
                    })}
                    alt="Hotspot avatar"
                  />
                  {hotspot && (
                    <StatusDot
                      hotspot={hotspot}
                      withoutCustomRewardScaling
                      className="absolute top-0 right-0 block"
                    />
                  )}
                </span>
              </div>
              <div className="flex-1 min-w-0 focus:outline-none">
                <span className="absolute inset-0" aria-hidden="true" />
                <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
                  {hotspot
                    ? formatHotspotName(hotspot)
                    : truncateText(hotspot_uuid, 20)}
                </p>
                <p className="text-sm font-normal text-gray-500 truncate dark:text-gray-400">
                  {location.name}
                </p>
                <DisplayLabels linkedUuid={uuid} size="small" />
              </div>
            </Link>
          </td>

          <td className="px-6 py-4 whitespace-nowrap">
            {formatDateToLocale(installed_at)}
          </td>

          <td className="px-6 py-4 whitespace-nowrap">
            {uninstalled_at ? formatDateToLocale(installed_at) : "-"}
          </td>

          <td className="px-6 py-4 whitespace-nowrap">
            <MemberCell
              memberUuid={created_by}
              subtitle={formatDateToLocale(installed_at)}
            />
          </td>
        </tr>
      );
    },
    [hotspotsMap, locations]
  );

  return (
    <div className="shadow w-full overflow-x-scroll sm:rounded-lg">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-500">
        <thead className="bg-gray-50 text-gray-500 dark:bg-gray-700 dark:text-gray-300">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
            >
              <div className="flex items-center">
                <span className="mr-1">
                  {installs.length} linked install
                  {installs.length > 1 ? "s" : ""}
                </span>
              </div>
            </th>

            <th
              scope="col"
              style={{ minWidth: "9rem" }}
              className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
            >
              Installed
            </th>

            <th
              scope="col"
              style={{ minWidth: "9rem" }}
              className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
            >
              Uninstalled
            </th>

            <th
              scope="col"
              style={{ minWidth: "13rem" }}
              className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
            >
              Added
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800">
          {installs.map(renderInstallTableRow)}
        </tbody>
      </table>
    </div>
  );
};

export default LinkedInstallsTable;
